import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { forkJoin } from "rxjs";
import { SampleTypeEnum } from "src/app/enums/sampleType.enum";
import { HazardCategory } from "src/app/enums/hazardCategory.enum";
import { Agent } from "src/app/models/agent.model";
import { AgentService } from "src/app/services/agent.service";
import { MasterAgentService } from "src/app/services/master-agent.service";
import { OrganizationLevelService } from "src/app/services/organizationLevel.service";
import { TypeService } from "src/app/services/type.service";
import { ColumnSpan } from "src/app/shared/form/columnSpan.model";
import {
    FormComponent,
    IFormDialogData,
} from "src/app/shared/form/form.component";
import {
    FormField,
    HierarchicalSelectionFormField,
    NumberFormField,
    SingleSelectionFormField,
    TextFormField,
} from "src/app/shared/form/formField.model";
import { Lookup } from "src/app/models/lookup.model";
import nameOf from "src/app/shared/form/nameOf.helper";
import { OperatorTypeEnum } from "src/app/enums/operatorType.enum";
import { OrganizationLevel } from "src/app/models/organizationLevel.model";
import { OrgLevelTypeEnum } from "src/app/enums/orgLevelType.enum";
import { toOrganizationTree } from "src/app/_helpers/org-tree-helper";

@Component({
  standalone: false,
    selector: "app-agent-dialog",
    templateUrl: "./../../shared/form/form.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./../../shared/form/form.component.scss"],
})
export class AgentDialogComponent
    extends FormComponent<Agent>
    implements OnInit
{
    organizationLevels: OrganizationLevel[];
    agentTypes: Lookup[] = [];
    fields: FormField[];
    masterAgents: Lookup[];

    constructor(
        dialogRef: MatDialogRef<AgentDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IFormDialogData,
        agentService: AgentService,
        private typeService: TypeService,
        private organizationLevelService: OrganizationLevelService,
        private masterAgentService: MasterAgentService,
        protected injector: Injector,
    ) {
        super(dialogRef, agentService, data, "Agent", injector);
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        const orgPayload = JSON.stringify({
            OperatorTypeEnum: JSON.stringify({
                Condition: OperatorTypeEnum.Equal,
                Field: nameOf<OrganizationLevel>((x) => x.orgLevelType),
                Value: `${OrgLevelTypeEnum.Site},${OrgLevelTypeEnum.Function},${OrgLevelTypeEnum.BusinessUnit},${OrgLevelTypeEnum.Corporate}`,
            }),
        });
        forkJoin({
            entity: this.getEntity(),
            organizationLevels:
                this.organizationLevelService.getAll(orgPayload),
            masterAgents: this.masterAgentService.getAll(),
        }).subscribe(
            (result) => {
                this.entity = result.entity;
                this.organizationLevels = result.organizationLevels;
                this.masterAgents = result.masterAgents;
                this.initFields();
                this.isFormLoading = false;
            },
            (error) => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            },
        );
    }

    getNewObject(): Agent {
        return new Agent();
    }

    validate(): boolean {
        return true;
    }

    initFields(): void {
        this.fields = [
            new SingleSelectionFormField({
                property: nameOf<Agent>((x) => x.masterAgentId),
                name: "Master Agent",
                columnSpan: new ColumnSpan(1, 2),
                options: this.masterAgents,
                onChange: (selected) => this.updateFields(selected.value),
            }),
            new TextFormField({
                property: nameOf<Agent>((x) => x.name),
                name: "Agent Name",
                columnSpan: new ColumnSpan(1, 2),
            }),
            new TextFormField({
                property: nameOf<Agent>((x) => x.casId),
                name: "CAS Id",
                columnSpan: new ColumnSpan(1, 2),
            }),
            new TextFormField({
                property: nameOf<Agent>((x) => x.agentForm),
                name: "Agent Fraction",
                required: false,
                columnSpan: new ColumnSpan(1, 2),
            }),
            new SingleSelectionFormField({
                property: nameOf<Agent>((x) => x.agentType),
                options: this.typeService.getEnumOptions(SampleTypeEnum),
                name: "Agent Type",
                columnSpan: new ColumnSpan(1, 2),
                readonly:
                    this.entity.id && this.entity.agentType != null
                        ? true
                        : false,
            }),
            new SingleSelectionFormField({
                property: nameOf<Agent>((x) => x.hazardCategory),
                options: this.typeService.getEnumOptions(HazardCategory),
                name: "Hazard Category",
                required: false,
                columnSpan: new ColumnSpan(1, 2),
            }),
            new TextFormField({
                property: nameOf<Agent>((x) => x.healthAdvisory),
                name: "Health Advisory",
                required: false,
                columnSpan: new ColumnSpan(1, 2),
            }),
            new NumberFormField({
                property: nameOf<Agent>((x) => x.consequenceRating),
                name: "Consequence Rating",
                required: false,
                columnSpan: new ColumnSpan(1, 2),
            }),
            new HierarchicalSelectionFormField({
                property: nameOf<Agent>((x) => x.organizationLevelId),
                options: toOrganizationTree(this.organizationLevels),
                name: "Visibility",
                columnSpan: new ColumnSpan(1, 2),
                required: true,
            }),
            new TextFormField({
                property: nameOf<Agent>((x) => x.specificRecommendations),
                name: "Specific Recommendations",
                required: false,
            }),
        ];
    }

    updateFields(value: number) {
        this.masterAgentService.getById(value).subscribe((masterAgent) => {
            const {
                healthAdvisory,
                casId,
                agentType,
                agentForm,
                specificRecommendations,
                name,
                hazardCategory,
            } = masterAgent;
            this.entity = {
                ...this.entity,
                healthAdvisory,
                casId,
                agentType,
                agentForm,
                specificRecommendations,
                name,
                // DASH-3168: pre-fill from the master; the field stays editable so the tenant can override.
                hazardCategory,
            };
        });
    }
}
